<script lang="ts">
  import { startCase } from "lodash";
  import type { Distributor, DistributorAccount } from "@shared/types/Distributor";
  import type { SalesRep } from "@shared/types/SalesRep";
  import formatPhone from "@shared/filters/phone.js";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { formatContactString } from "@retailer/services/SalesRepService";
  import ChatWithSupportLink from "@shared/svelte/Components/ChatWithSupportLink.svelte";
  import { VT_802_SPIRITS_DISTRIBUTOR_ID } from "@shared/services/retailer_user_authentication/constants";

  export let selectedDistributor: Distributor;
  export let account: DistributorAccount;
  export let accountSalesReps: SalesRep[];
  export let accountNumberIsInvalid: boolean;
  export let accountNumber: string;
  export let closeModal: () => void;

  const label =
    selectedDistributor?.id === VT_802_SPIRITS_DISTRIBUTOR_ID
      ? "Third class liquor license"
      : "Account number";
  const helperText =
    selectedDistributor?.id === VT_802_SPIRITS_DISTRIBUTOR_ID
      ? "Third Class License number begins with “LP-” (for example, LP-012345)"
      : "This distributor requires an account number to send orders.";
</script>

{#if !account}
  <div class="grid-row margin-top--small">
    <div class="grid-col c12-12">
      <div class="default-body--bold">{startCase(label)}</div>
    </div>
    <div class="grid-col c12-12 margin-top--smallest">
      <input
        type="text"
        name="account_number"
        class="fizz-input-text fizz-full-width"
        bind:value={accountNumber}
        data-testid="account-number-input"
        class:fizz-input-error={accountNumberIsInvalid}
        on:focus={() => {
          accountNumberIsInvalid = false;
        }}
      />
      {#if accountNumberIsInvalid}
        <p class="fizz-flex-row-4 fizz-text-urgent fizz-text-medium fizz-text-regular">
          <Icon type="Error" height={20} width={16} />
          <span>{accountNumber ? `Invalid ${label.toLowerCase()}` : `${label} is required`}</span>
        </p>
      {/if}
    </div>
    {#if !accountNumberIsInvalid}
      <div class="grid-col c12-12 margin-top--smallest fizz-text-subdued">
        {helperText}
      </div>
    {/if}
  </div>
  <div class="fizz-relative margin-top--small">
    <div class="fizz-surface-gray fizz-inset-square-16 fizz-radius-medium">
      <div
        class="fizz-text-small fizz-stack-12 fizz-text-strong fizz-flex-row-4 fizz-align-content-start"
      >
        <Icon type="Lightbulb" extraClassNames="fizz-icon-16 float-left" />
        <span>Here are some ways to find your {label.toLowerCase()}</span>
      </div>
      <ol class="fizz-ordered-list fizz-flow-8 fizz-text-small">
        {#if selectedDistributor?.id === VT_802_SPIRITS_DISTRIBUTOR_ID}
          <li>On your printed license in your establishment</li>
          <li>In your license issuance email from the Department of Liquor and Lottery (DLL)</li>
          <li
            >In the <a
              href="https://dllportal.my.vermont.gov/DLLLicenseManagment/s/login/"
              target="_blank"
              rel="noreferrer">DLL Licensing Portal</a
            ></li
          >
          <li>Contact Vermont Dept. of Liquor and Lottery: (802) 828-2339</li>
        {:else}
          <li>Account number information can be found on distributor invoices</li>
          <li>
            Contact your distributor: {formatPhone(selectedDistributor.contact_phone)}
          </li>
          <li>
            Still having trouble? <ChatWithSupportLink
              message="Let Us Know."
              bold={false}
              onClick={closeModal}
            />
          </li>
        {/if}
      </ol>
    </div>
  </div>
{:else}
  <div class="grid-row margin-top--small">
    <div class="grid-col c12-12">
      <div class="default-body--bold">Account Information</div>
    </div>
    <div class="grid-col c12-12 margin-top--smaller">
      <div class="default-body--regular">
        Account #: {account.account_number}
      </div>
      <div class="default-body--regular">
        {account.name}
      </div>
      <div class="default-body--regular">
        {account.address1}
      </div>
      <div class="default-body--regular">
        {account.city}, {account.state}
        {account.zip}
      </div>
    </div>
  </div>
  <div class="grid-row margin-top--small">
    <div class="grid-col c12-12">
      <div class="default-body--bold">Sales Reps</div>
    </div>
    <div class="grid-col c12-12 margin-top--smaller">
      {#each accountSalesReps as rep}
        <div class="default-body--regular">{formatContactString(rep, false)}</div>
      {/each}
    </div>
  </div>
  <div class="fizz-relative margin-top--small align-center">
    Need help? <ChatWithSupportLink message="Let Us Know." bold={false} onClick={closeModal} />
  </div>
{/if}
